import React, { useMemo } from 'react'

import { QueryClientProvider } from '@tanstack/react-query'
import { Route, Routes } from 'react-router-dom'

import { array, bool, object, shape, string } from 'prop-types'

import LazyAiSignInModal from 'Pods/LazyLoaded/AiSignInModal/LazyAiSignInModal'
import ContextBasedRouter from 'Pods/Shared/ContextBasedRouter'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'

import queryClient from 'Utils/queryClient'
import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import BestOnDesktop from './Components/BestOnDesktop/BestOnDesktop'
import LazyCommunityGalleryPanel from './Components/LazyCommunityGalleryPanel/LazyCommunityGalleryPanel'
import LazyConferenceSignInModal from './Components/LazyConferenceSignInModal/LazyConferenceSignInModal'
import MainPanel from './Components/MainPanel/MainPanel'
import MobileTabBar from './Components/MobileTabBar/MobileTabBar'
import Sidebar from './Components/Sidebar/Sidebar'
import UsageModal from './Components/UsageModal/UsageModal'
import ViewerSidebar from './Components/ViewerSidebar/ViewerSidebar'
import useActiveModelSchema from './Hooks/useActiveModelSchema'
import useAuthModalTrigger from './Hooks/useAuthModalTrigger'
import useCheckUnderControl from './Hooks/useCheckUnderControl'
import useClearReviewedOnOpen from './Hooks/useClearReviewedOnOpen'
import useDeepLinkGeneration from './Hooks/useDeepLinkGeneration'
import useGalleryOriginSync from './Hooks/useGalleryOriginSync'
import useHydrateGenerationQuota from './Hooks/useHydrateGenerationQuota'
import useInitialMobilePanel from './Hooks/useInitialMobilePanel'
import useLoadInitialCredits from './Hooks/useLoadInitialCredits'
import useModelUrlSync from './Hooks/useModelUrlSync'
import usePageTitleSync from './Hooks/usePageTitleSync'
import usePendingGeneratorRun from './Hooks/usePendingGeneratorRun'
import useResumePendingGeneration from './Hooks/useResumePendingGeneration'
import { closeSignInModal } from './Store/actions/ui'
import ThreeDStudioPageContext from './ThreeDStudioPageContext'

const EMPTY_FILTER_OPTIONS = {
  visibleCategories: EMPTY_ARRAY,
  sortOptions: EMPTY_ARRAY,
}

function ThreeDStudio({
  locale = 'en',
  initialPathname = '',
  ...props
}) {
  // route_translator serves the studio under a lowercase locale prefix (/pt/ai,
  // /zh-cn/ai). Mounting the router on that prefix keeps every in-app path
  // ('/ai', '/ai-3d-model-generator/3d-models') locale-agnostic while Link and
  // navigate() stay on the visitor's locale.
  const basename = locale === 'en' ? '/' : `/${locale.toLowerCase()}`

  return (
    <ContextBasedRouter basename={basename} initialPathname={initialPathname}>
      {/* ThreeDStudioContent is wrapped in a QueryClientProvider because the library
       (LibraryContent inside Sidebar) fetches its items via react-query. */}
      <QueryClientProvider client={queryClient}>
        <ThreeDStudioContent locale={locale} {...props} />
      </QueryClientProvider>
    </ContextBasedRouter>
  )
}

// Per-user values (userLoggedIn, the two Flipper flags) arrive via usePageConfig
// - the same channel UnifiedNavigation uses - and not via SSR props, because
// this page is rendered by FastApplicationController and its HTML can be served
// from a shared cache. The library itself loads client-side from the
// authenticated /api/internal/generator/sessions endpoint (see useGetLibraryItems).
function ThreeDStudioContent({
  locale = 'en',
  extractingCoreStyles = false,
  initialRunSlug = null,
  dccSoftwares = EMPTY_ARRAY,
  communityGalleryFilterOptions = EMPTY_FILTER_OPTIONS,
  communityGalleryInitialModels = null,
  conferenceMode = null,
}) {
  const pageConfig = usePageConfig({ listener: 'full-page-config-loaded', checkIfAvailable: false })
  const enableHunyuanSegmentation = !!pageConfig?.enableHunyuanSegmentation
  const enableMeshyRemesh = !!pageConfig?.enableMeshyRemesh

  useActiveModelSchema({ enableHunyuanSegmentation, enableMeshyRemesh })
  usePendingGeneratorRun()
  useHydrateGenerationQuota()
  useCheckUnderControl()
  useLoadInitialCredits()
  useGalleryOriginSync()
  useDeepLinkGeneration()
  useModelUrlSync(initialRunSlug)
  usePageTitleSync()
  useClearReviewedOnOpen()
  useAuthModalTrigger()
  useResumePendingGeneration()
  useInitialMobilePanel({ extractingCoreStyles })

  const openedModelFileUrl = useThreeDStudioStore(
    state => !!state.ui?.openedModel?.outputAssets?.[0]?.fileUrl
  )
  const uploadedModelUrl = useThreeDStudioStore(
    state => state.modelSettings?.inputThreeDUrl
  )
  const isModelOpened = Boolean(openedModelFileUrl || uploadedModelUrl)

  const activeMobilePanel = useThreeDStudioStore(state => state.ui?.activeMobilePanel)
  const isSignInModalOpen = useThreeDStudioStore(state => state.ui?.isSignInModalOpen)
  const signInModalMode = useThreeDStudioStore(state => state.ui?.signInModalMode)
  const signInModalStep = useThreeDStudioStore(state => state.ui?.signInModalStep)
  const pageConfigLoaded = pageConfig !== undefined
  const userLoggedIn = !!pageConfig?.isCurrentUser
  const showGenerationErrorDetails = !!pageConfig?.showGenerationErrorDetails
  const modelReadyToasterEnabled = !!pageConfig?.modelReadyToasterEnabled
  const segmentationFlowEnabled = !!pageConfig?.segmentationFlowEnabled
  const designerAssistEnabled = !!pageConfig?.designerAssistEnabled
  const ideationFlowEnabled = !!pageConfig?.ideationFlowEnabled
  const imageSearchOnIdeationFLowEnabled = !!pageConfig?.imageSearchOnIdeationFLowEnabled

  const pageContextValue = useMemo(
    () => ({
      locale,
      userLoggedIn,
      pageConfigLoaded,
      extractingCoreStyles: !!extractingCoreStyles,
      dccSoftwares,
      showGenerationErrorDetails,
      modelReadyToasterEnabled,
      segmentationFlowEnabled,
      enableHunyuanSegmentation,
      enableMeshyRemesh,
      designerAssistEnabled,
      ideationFlowEnabled,
      imageSearchOnIdeationFLowEnabled,
      communityGalleryFilterOptions,
      communityGalleryInitialModels,
    }),
    [
      locale,
      userLoggedIn,
      pageConfigLoaded,
      extractingCoreStyles,
      dccSoftwares,
      showGenerationErrorDetails,
      modelReadyToasterEnabled,
      segmentationFlowEnabled,
      enableHunyuanSegmentation,
      enableMeshyRemesh,
      designerAssistEnabled,
      ideationFlowEnabled,
      imageSearchOnIdeationFLowEnabled,
      communityGalleryFilterOptions,
      communityGalleryInitialModels,
    ]
  )

  return (
    <ThreeDStudioPageContext.Provider value={pageContextValue}>
      <div className="three-d-studio">
        <Sidebar />
        <Routes>
          <Route
            path="/ai-3d-model-generator/3d-models"
            element={(
              <div className="three-d-studio__gallery-main">
                <LazyCommunityGalleryPanel />
              </div>
            )}
          />
          <Route
            path="*"
            element={(
              <div className="three-d-studio__workspace" data-active-panel={activeMobilePanel}>
                <MobileTabBar isModelOpened={isModelOpened} />
                <MainPanel hasViewerSidebar />
                <ViewerSidebar isModelOpened={isModelOpened} />
              </div>
            )}
          />
        </Routes>
        <UsageModal />
        <BestOnDesktop />
        {conferenceMode && !userLoggedIn && (
          <LazyConferenceSignInModal eventName={conferenceMode.eventName} />
        )}
        {isSignInModalOpen && !userLoggedIn && (
          <LazyAiSignInModal mode={signInModalMode} step={signInModalStep} onClose={closeSignInModal} />
        )}
      </div>
    </ThreeDStudioPageContext.Provider>
  )
}

ThreeDStudio.propTypes = {
  locale: string,
  extractingCoreStyles: bool,
  initialPathname: string,
  initialRunSlug: string,
  dccSoftwares: array,
  communityGalleryFilterOptions: shape({
    visibleCategories: array,
    sortOptions: array,
  }),
  communityGalleryInitialModels: shape({
    data: array,
    meta: object,
  }),
  conferenceMode: shape({
    eventName: string.isRequired,
  }),
}

ThreeDStudioContent.propTypes = {
  locale: string,
  extractingCoreStyles: bool,
  initialRunSlug: string,
  dccSoftwares: array,
  communityGalleryFilterOptions: shape({
    visibleCategories: array,
    sortOptions: array,
  }),
  communityGalleryInitialModels: shape({
    data: array,
    meta: object,
  }),
  conferenceMode: shape({
    eventName: string.isRequired,
  }),
}

export default ThreeDStudio
